import { FC, MutableRefObject, useEffect, useRef } from 'react'

import {
  ArrowCirclePathRightIcon,
  ArrowLeftIcon,
  ChainLinkIcon,
  IconSizes,
  MultiplyIcon,
  SquareArrowTopRightLineIcon,
  StarsLineIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { Spinner, SpinnerSizes } from '@inno-ui/spinner'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { artifactViewUrl } from 'shared/api/forgeApi'

import { useArtifactBridge } from '../../hooks/useArtifactBridge'
import { ArtifactView } from '../../types'
import { ArtifactUnavailable } from '../ArtifactUnavailable'
import { WidgetOverlay } from '../WidgetOverlay'
import styles from './ArtifactFrame.module.scss'

interface ArtifactFrameProps {
  publicId: string
  view: ArtifactView
  isInFullView?: boolean
  onToggleFullView?: () => void
  onBack?: () => void
  onRefine?: () => void
  onShare?: () => void
  onDelete?: () => void
  onRemoveFromDesktop?: () => void
  sharedWithYou?: boolean
  sharedByName?: string | null
  context?: unknown
  title?: string
  hideBar?: boolean
  reloadRef?: MutableRefObject<(() => void) | undefined>
  noOverlay?: boolean
}

const ICON = IconSizes.Size16

export const ArtifactFrame: FC<ArtifactFrameProps> = ({
  publicId,
  view,
  isInFullView,
  onToggleFullView,
  onBack,
  onRefine,
  onShare,
  onDelete,
  onRemoveFromDesktop,
  sharedWithYou,
  sharedByName,
  context,
  title,
  hideBar,
  reloadRef,
  noOverlay
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })

  const { reload, status, retry } = useArtifactBridge({
    iframeRef,
    view,
    context,
    onRefine
  })
  // No URL means forge is unconfigured for this environment. Mounting the
  // iframe anyway would point it at the container's own origin, so surface it
  // as unavailable instead — see artifactViewUrl.
  const viewUrl = artifactViewUrl(publicId)
  const isOffline = status === 'error' || !viewUrl
  const isLoading = status === 'loading' && Boolean(viewUrl)

  // expose reload to bar-less owners (mini tile right-click menu)
  useEffect(() => {
    if (reloadRef) reloadRef.current = reload
    return () => {
      if (reloadRef?.current === reload) reloadRef.current = undefined
    }
  }, [reload, reloadRef])

  const frame = (
    <div className={styles.frame}>
      {!hideBar && (
        <div className={cn(styles.bar, { [styles.barDocked]: isInFullView })}>
          {onBack && (
            <button
              type='button'
              className={cn(styles.barBtn, styles.barBtnLabelled)}
              onClick={onBack}
            >
              <ArrowLeftIcon size={ICON} />
              {t('back')}
            </button>
          )}
          {title && <span className={styles.barTitle}>{title}</span>}
          {sharedWithYou && (
            <span className={styles.barFlag}>
              {sharedByName
                ? t('sharedBy', { name: sharedByName })
                : t('sharedWithYou')}
            </span>
          )}

          <button
            type='button'
            className={styles.barBtn}
            onClick={reload}
            title={t('refresh')}
            aria-label={t('refresh')}
          >
            <ArrowCirclePathRightIcon size={ICON} />
          </button>
          {onRefine && (
            <button
              type='button'
              className={styles.barBtn}
              onClick={onRefine}
              title={t('refine')}
              aria-label={t('refine')}
            >
              <StarsLineIcon size={ICON} />
            </button>
          )}
          {onShare && (
            <button
              type='button'
              className={styles.barBtn}
              onClick={onShare}
              title={t('share')}
              aria-label={t('share')}
            >
              <ChainLinkIcon size={ICON} />
            </button>
          )}
          {/* delete separated so it doesn't sit flush against safe tools */}
          {onDelete && (
            <>
              <span className={styles.divider} aria-hidden='true' />
              <button
                type='button'
                className={cn(styles.barBtn, styles.barBtnDanger)}
                onClick={onDelete}
                title={t('deleteForEveryone')}
                aria-label={t('deleteForEveryone')}
              >
                <TrashLineIcon size={ICON} />
              </button>
            </>
          )}

          <span className={styles.spacer} />

          {isInFullView ? (
            // × exits full screen, doesn't remove the tile
            onToggleFullView && (
              <button
                type='button'
                className={styles.barBtn}
                onClick={onToggleFullView}
                title={t('exitFullView')}
                aria-label={t('exitFullView')}
              >
                <MultiplyIcon size={ICON} />
              </button>
            )
          ) : (
            // × unpins from my desktop only, not the red trash
            <>
              {onToggleFullView && (
                <button
                  type='button'
                  className={styles.barBtn}
                  onClick={onToggleFullView}
                  title={t('maximize')}
                  aria-label={t('maximize')}
                >
                  <SquareArrowTopRightLineIcon size={ICON} />
                </button>
              )}
              {onRemoveFromDesktop && (
                <button
                  type='button'
                  className={styles.barBtn}
                  onClick={onRemoveFromDesktop}
                  title={t('removeFromDesktop')}
                  aria-label={t('removeFromDesktop')}
                >
                  <MultiplyIcon size={ICON} />
                </button>
              )}
            </>
          )}
        </div>
      )}
      {viewUrl && (
        <iframe
          ref={iframeRef}
          className={styles.iframe}
          src={viewUrl}
          title={title || 'AI artifact'}
          sandbox='allow-scripts allow-same-origin allow-popups allow-forms allow-downloads'
        />
      )}
      {/* Interactive tiles need a shield over the iframe; marketplace previews
          omit it so drag gestures reach the draggable ancestor. */}
      {!isInFullView && onToggleFullView && (
        <button
          type='button'
          className={styles.clickShield}
          aria-label={t('maximize')}
          onClick={onToggleFullView}
        />
      )}
      {/* spinner until iframe reports ready, non-blocking so tile stays draggable */}
      {isLoading && (
        <div className={styles.loading}>
          <Spinner size={SpinnerSizes.M} />
        </div>
      )}
      {/* forge unreachable, pointer-events:none so tile stays draggable */}
      {isOffline && (
        <div className={styles.offline}>
          <ArtifactUnavailable onRetry={retry} compact={!isInFullView} />
        </div>
      )}
    </div>
  )

  if (isInFullView && !noOverlay) {
    return (
      <WidgetOverlay onClose={onToggleFullView} ariaLabel={title}>
        {frame}
      </WidgetOverlay>
    )
  }
  return frame
}
